import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { WindowFrame } from './WindowFrame';
import { MessageFrame } from './MessageFrame';
import { NotepadFrame } from './NotepadFrame';
import { SolidFrame } from './SolidFrame';
import { TipTapEditor } from './TipTapEditor';
import { TextFormatToolbar } from '../TextFormatToolbar';
import { BaseNodeWrapper } from './BaseNodeWrapper';
import { NodeFrameWrapper } from './NodeFrameWrapper';
import { getTextColor } from '../../utils/colorContrast';
import type { TextNodeData } from '../../types';

interface TextNodeProps {
  id: string;
  content: string;
  frameStyle: 'browser' | 'none' | 'message' | 'notepad' | 'solid';
  style?: {
    fontSize?: string;
    fontFamily?: string;
    color?: string;
    textAlign?: 'left' | 'center' | 'right';
    fontStyle?: 'normal' | 'italic';
    fontWeight?: string;
    verticalAlign?: 'top' | 'middle' | 'bottom';
  };
  senderName?: string;
  avatarUrl?: string;
  avatarEmoji?: string;
  backgroundColor?: string;
  workInProgress?: boolean;
  borderRadius?: number;
  wipBannerOffset?: number;
  isEditing?: boolean;
  onUpdate?: (updates: Partial<TextNodeData>) => void;
}

export type { TextNodeData };

export const TextNode: React.FC<TextNodeProps> = ({
  content,
  frameStyle,
  style = {},
  senderName = 'Alan Smithee',
  avatarUrl = '/avatar.png',
  avatarEmoji,
  backgroundColor,
  workInProgress = false,
  borderRadius = 8,
  wipBannerOffset = 0,
  isEditing = false,
  onUpdate
}) => {
  const [localContent, setLocalContent] = useState(content);
  const [localSenderName, setLocalSenderName] = useState(senderName);
  const [editor, setEditor] = useState<Editor | null>(null);
  const originalContent = useRef(content);

  useEffect(() => {
    setLocalContent(content);
    originalContent.current = content;
  }, [content]);

  const handleContentUpdate = (newContent: string) => {
    setLocalContent(newContent);
    onUpdate?.({ content: newContent });
  };

  const handleSenderNameUpdate = (newName: string) => {
    const finalName = newName.trim() || 'Alan Smithee';
    setLocalSenderName(finalName);
    onUpdate?.({ senderName: finalName });
  };

  const defaultColor = frameStyle === 'notepad'
    ? '#636363'
    : backgroundColor
    ? getTextColor(backgroundColor)
    : (frameStyle === 'none' ? '#ffffff' : frameStyle === 'message' ? '#000000' : '#555555');

  const textStyles = {
    fontSize: style.fontSize || '48px',
    fontFamily: style.fontFamily || 'Test National',
    color: style.color || defaultColor,
    textAlign: style.textAlign || 'center',
    fontStyle: style.fontStyle || 'normal',
    fontWeight: style.fontWeight || '400',
    lineHeight: frameStyle === 'message' ? 1.4 : 1.2,
  };

  // Helper to check if content is TipTap JSON
  const isTipTapContent = (content: string): boolean => {
    try {
      const parsed = JSON.parse(content);
      return parsed.type === 'doc' && Array.isArray(parsed.content);
    } catch {
      return false;
    }
  };

  // Convert old format to TipTap JSON if needed
  const getEditorContent = (): string => {
    if (isTipTapContent(localContent)) {
      return localContent;
    }

    // Convert plain text with style to TipTap JSON
    const marks = [];
    if (style.fontWeight === 'bold' || parseInt(style.fontWeight || '400') >= 700) {
      marks.push({ type: 'bold' });
    }
    if (style.fontStyle === 'italic') {
      marks.push({ type: 'italic' });
    }

    const textStyleAttrs: Record<string, string> = {};
    if (style.fontSize) textStyleAttrs.fontSize = style.fontSize;
    if (style.fontFamily) textStyleAttrs.fontFamily = style.fontFamily;

    if (Object.keys(textStyleAttrs).length > 0) {
      marks.push({ type: 'textStyle', attrs: textStyleAttrs });
    }

    return JSON.stringify({
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          attrs: { textAlign: style.textAlign || 'center' },
          content: [
            {
              type: 'text',
              text: localContent || 'New text node',
              marks: marks.length > 0 ? marks : undefined,
            },
          ],
        },
      ],
    });
  };

  const getVerticalAlignment = (): 'flex-start' | 'center' | 'flex-end' => {
    const vAlign = style.verticalAlign || 'middle';
    switch (vAlign) {
      case 'top':
        return 'flex-start';
      case 'bottom':
        return 'flex-end';
      default:
        return 'center';
    }
  };

  const handleVerticalAlignChange = useCallback((align: 'top' | 'middle' | 'bottom') => {
    onUpdate?.({ style: { ...style, verticalAlign: align } });
  }, [style, onUpdate]);

  const textContent = (
    <TipTapEditor
      content={getEditorContent()}
      isEditing={isEditing}
      onUpdate={handleContentUpdate}
      style={textStyles}
      className={frameStyle === 'message' ? 'text-[17px]' : ''}
      onEditorReady={setEditor}
    />
  );

  // Helper to extract plain text from TipTap JSON for MessageFrame
  const getPlainTextContent = (): string => {
    if (isTipTapContent(localContent)) {
      try {
        const parsed = JSON.parse(localContent);
        // Extract text from TipTap JSON structure
        const extractText = (node: any): string => {
          if (node.type === 'text') {
            return node.text || '';
          }
          if (node.content && Array.isArray(node.content)) {
            return node.content.map(extractText).join('');
          }
          return '';
        };
        return extractText(parsed);
      } catch {
        return localContent;
      }
    }
    return localContent;
  };

  const handleMessageContentUpdate = (newContent: string) => {
    // For message frame, store plain text (not TipTap JSON)
    setLocalContent(newContent);
    onUpdate?.({ content: newContent });
  };

  if (frameStyle === 'message') {
    return (
      <div className="w-full h-full bg-white rounded-lg overflow-hidden" style={{ isolation: 'isolate' }}>
        <MessageFrame
          senderName={localSenderName}
          avatarUrl={avatarUrl}
          avatarEmoji={avatarEmoji}
          isEditing={isEditing}
          content={getPlainTextContent()}
          backgroundColor={backgroundColor}
          onSenderNameChange={setLocalSenderName}
          onSenderNameBlur={handleSenderNameUpdate}
          onContentChange={setLocalContent}
          onContentBlur={handleMessageContentUpdate}
          onAvatarClick={() => {
            console.log('Avatar click');
          }}
          onEmojiChange={(newEmoji) => {
            onUpdate?.({ avatarEmoji: newEmoji });
          }}
        />
      </div>
    );
  }

  if (frameStyle === 'notepad') {
    return (
      <BaseNodeWrapper
        workInProgress={workInProgress}
        wipBannerOffset={wipBannerOffset}
        isEditing={isEditing}
        onUpdate={onUpdate}
        overflow={isEditing ? 'visible' : 'hidden'}
      >
        {isEditing && editor && (
          <TextFormatToolbar
            editor={editor}
            verticalAlign={style.verticalAlign || 'middle'}
            onVerticalAlignChange={handleVerticalAlignChange}
          />
        )}
        <NotepadFrame borderRadius={borderRadius}>
          <div className="w-full h-full flex justify-center p-8" style={{ alignItems: getVerticalAlignment() }}>
            {textContent}
          </div>
        </NotepadFrame>
      </BaseNodeWrapper>
    );
  }

  if (frameStyle === 'solid') {
    return (
      <BaseNodeWrapper
        workInProgress={workInProgress}
        wipBannerOffset={wipBannerOffset}
        isEditing={isEditing}
        onUpdate={onUpdate}
        overflow={isEditing ? 'visible' : 'hidden'}
      >
        {isEditing && editor && (
          <TextFormatToolbar
            editor={editor}
            verticalAlign={style.verticalAlign || 'middle'}
            onVerticalAlignChange={handleVerticalAlignChange}
          />
        )}
        <SolidFrame backgroundColor={backgroundColor} borderRadius={borderRadius}>
          <div className="w-full h-full flex justify-center p-8" style={{ alignItems: getVerticalAlignment() }}>
            {textContent}
          </div>
        </SolidFrame>
      </BaseNodeWrapper>
    );
  }

  if (frameStyle === 'none') {
    return (
      <BaseNodeWrapper
        workInProgress={workInProgress}
        wipBannerOffset={wipBannerOffset}
        isEditing={isEditing}
        onUpdate={onUpdate}
        overflow={isEditing ? 'visible' : 'hidden'}
      >
        {isEditing && editor && (
          <TextFormatToolbar
            editor={editor}
            verticalAlign={style.verticalAlign || 'middle'}
            onVerticalAlignChange={handleVerticalAlignChange}
          />
        )}
        <NodeFrameWrapper borderRadius={borderRadius}>
          <div className="w-full h-full flex justify-center p-4" style={{ alignItems: getVerticalAlignment() }}>
            {textContent}
          </div>
        </NodeFrameWrapper>
      </BaseNodeWrapper>
    );
  }

  return (
    <BaseNodeWrapper
      workInProgress={workInProgress}
      wipBannerOffset={wipBannerOffset}
      isEditing={isEditing}
      onUpdate={onUpdate}
      overflow={isEditing ? 'visible' : 'hidden'}
    >
      {isEditing && editor && (
        <TextFormatToolbar
          editor={editor}
          verticalAlign={style.verticalAlign || 'middle'}
          onVerticalAlignChange={handleVerticalAlignChange}
        />
      )}
      <WindowFrame backgroundColor={backgroundColor} borderRadius={borderRadius}>
        <div className="w-full h-full flex justify-center p-8" style={{ alignItems: getVerticalAlignment() }}>
          {textContent}
        </div>
      </WindowFrame>
    </BaseNodeWrapper>
  );
};